// Open SLA breaches (PHASE25), shown under the header of every admin page to whoever may see SLA alerts.
// Refreshed by the realtime 'sla.alert' event and every 30 s.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check } from 'lucide-react';
import { api, errorText } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useCan } from '@/store/auth';
import { useLiveRefresh } from '@/hooks/live';
import { toast } from '@/store/toast';
import { fmtTime } from '@/lib/utils';
import { t } from '@/i18n';

interface Alert { id: number; queue_name: string; kind: 'SERVICE_LEVEL' | 'ABANDON' | 'LONG_WAIT'; latest_value: number; target_value: number; calls: number; opened_at: string; acked_by: string | null }
const KIND: Record<Alert['kind'], string> = { SERVICE_LEVEL: 'service level', ABANDON: 'abandon rate', LONG_WAIT: 'long wait' };

// two short tones when a new breach appears (browsers may stay silent until the page was clicked once)
function chime(): void {
  try {
    const ctx = new AudioContext();
    [0, 0.25].forEach((at, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = i ? 660 : 880;
      g.gain.setValueAtTime(0.15, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + at + 0.2);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + 0.2);
    });
    setTimeout(() => ctx.close().catch(() => undefined), 1000);
  } catch { /* no audio */ }
}

const MAX = 4;

export function SlaBanner() {
  const canView = useCan('sla.view');
  const canAck = useCan('sla.ack');
  const q = useQuery({ queryKey: ['sla-open'], enabled: canView, refetchInterval: 30000,
                       queryFn: () => api<{ rows: Alert[]; window_min: number }>('/sla/alerts', { query: { open: 1 } }) });
  useLiveRefresh(['sla.alert'], () => { if (canView) q.refetch(); });
  const rows = q.data?.rows ?? [];
  // chime for alerts not seen before (not for those already open when the page loaded)
  const seen = React.useRef<Set<number> | null>(null);
  React.useEffect(() => {
    if (!q.data) return;
    const ids = q.data.rows.filter((a) => !a.acked_by).map((a) => a.id);
    if (seen.current && ids.some((id) => !seen.current!.has(id))) chime();
    seen.current = new Set([...(seen.current ?? []), ...ids]);
  }, [q.data]);
  if (!canView || rows.length === 0) return null;
  const fresh = rows.filter((a) => !a.acked_by);
  // the newest first; at most MAX lines, the rest are on the SLA report
  const shown = [...rows].sort((x, y) => (x.opened_at < y.opened_at ? 1 : x.opened_at > y.opened_at ? -1 : y.id - x.id)).slice(0, MAX);
  async function ack(a: Alert) {
    try { await api(`/sla/alerts/${a.id}/ack`, { method: 'POST' }); q.refetch(); } catch (e) { toast.error(errorText(e)); }
  }
  return (
    <div className={`border-b px-4 py-2 text-sm ${fresh.length ? 'bg-destructive/10' : 'bg-muted/40'}`} data-testid="sla-banner">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <AlertTriangle className={`h-4 w-4 shrink-0 ${fresh.length ? 'text-destructive' : 'text-muted-foreground'}`} />
        <b>{rows.length === 1 ? t('A queue is missing its SLA') : t('{{n}} SLA breaches', { n: rows.length })}</b>
        <span className="text-muted-foreground">{t('last {{n}} min', { n: q.data?.window_min ?? 60 })}</span>
        <Link to="/admin/reports?tab=sla" className="ml-auto text-sm underline">{t('SLA report')}</Link>
      </div>
      <ul className="mt-1 grid gap-1">
        {shown.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-x-2" data-testid={`sla-alert-${a.id}`}>
            <span className={a.acked_by ? 'text-muted-foreground' : ''}>
              <b>{a.queue_name}</b>: {a.kind === 'SERVICE_LEVEL'
                ? t('service level {{value}}% (target {{target}}%) · {{calls}} calls', { value: Number(a.latest_value), target: Number(a.target_value), calls: a.calls })
                : a.kind === 'ABANDON'
                  ? t('abandon rate {{value}}% (maximum {{target}}%) · {{calls}} calls', { value: Number(a.latest_value), target: Number(a.target_value), calls: a.calls })
                  : t('a caller waiting {{value}} s (maximum {{target}} s) · {{calls}} waiting that long', { value: Number(a.latest_value), target: Number(a.target_value), calls: a.calls })}
              {' '}· {t('since {{time}}', { time: fmtTime(a.opened_at) })}
            </span>
            {a.acked_by
              ? <span className="text-xs text-muted-foreground">{t('acknowledged by {{name}}', { name: a.acked_by })}</span>
              : canAck && <Button size="sm" variant="outline" className="h-7" onClick={() => ack(a)} aria-label={t('Acknowledge {{queue}} {{kind}}', { queue: a.queue_name, kind: t(KIND[a.kind]) })}><Check /> {t('Acknowledge')}</Button>}
          </li>
        ))}
      </ul>
      {rows.length > shown.length && <p className="mt-1 text-xs text-muted-foreground">{t('and {{n}} more on the SLA report', { n: rows.length - shown.length })}</p>}

    </div>
  );
}
